import Avvvatars from "avvvatars-react";
import { Link, useParams } from "react-router-dom";
import { prof_routes } from "../../../router/all_routes";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import { useEffect, useMemo, useState } from "react";
import { getCourseByID } from "../../../../api/edt";
import { toast } from "sonner";
import SuivisCourseModal from "./suivisCourseModal";
import AbsenceCourseModal from "./absenceCourseModal";
import { CourseState, CourseStatus } from "../../../edt/scheduler";
import {
  createFileAttachement,
  deleteFileAttachementByID,
  getFileAttachementByEntityId,
} from "../../../../api/fileAttachement";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { getSessionsByYear } from "../../../../api/settingEDT";
import AssignmentsManager from "./AssignmentsManager";
import { openSupportInNewTab } from "../../../../helper/supportHelper";

const TeacherCourseDetails = () => {
  const { selectedYear } = useAcademicYear();
  const routes = prof_routes;

  const { id } = useParams(); // On récupère l'id de l'URL

  const MySwal = withReactContent(Swal);

  const confirmDeleteSupport = (support: any) => {
    MySwal.fire({
      title: "Supprimer support ?",
      text: "Voulez-vous vraiment supprimer ce support ?",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          await deleteFileAttachementByID(
            support.id,
            buildPermissionContext(Resource.FILE_ATTACHEMENT, Action.DELETE),
          );
          MySwal.fire("Supprimé !", "Support supprimé.", "success");
          setSupports((prev) => prev.filter((s) => s.id !== support.id));
        } catch (error) {
          MySwal.fire("Erreur", "Impossible de supprimer le support.", "error");
        }
      }
    });
  };

  //Initialisation des state utilisés
  const [courseDetail, setCourseDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);
  const [followOpen, setFollowOpen] = useState(false);
  const [selectedCourseVersionId, setSelectedCourseVersionId] =
    useState<string>("");
  const [filesLoading, setFilesLoading] = useState<any>({});

  const [absenceOpen, setAbsenceOpen] = useState(false);

  const [allSessions, setAllSessions] = useState<any[]>([]);
  const [selectSession, setSelectSession] = useState<any>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>("Tous les mois");

  // === Gestion des supports du cours ===
  const [supports, setSupports] = useState<any[]>([]);

  const fetchCourseDetails = async () => {
    try {
      const response = await getCourseByID(id);
      const courseData = response.data || [];

      setCourseDetail(courseData);
      // console.log("Course", response);
    } catch (error: any) {
      // console.log("erreur de chargement du Cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les salles en cas d'erreur
      setCourseDetail(null);
    } finally {
      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    }
  };

  const fetchAllSessions = async () => {
    try {
      const response = await getSessionsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.STUDENT_ABSENCE, Action.READ),
      );
      // console.log("All Sessions", response);
      setAllSessions(response.data || []);
    } catch (error: any) {
      // console.log("erreur de chargement des sessions : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!id) return;
    setIsLoading(true);
    fetchCourseDetails();
  }, [refreshKey, id]);

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear) return;
    fetchAllSessions();
  }, [selectedYear]);

  const fetchSupports = async () => {
    try {
      const response = await getFileAttachementByEntityId(id, "Course");
      // console.log("Supports", response);
      setSupports(response.data || []);
    } catch (error: any) {
      // console.log("erreur de chargement des supports : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchSupports();
  }, [id]);

  const getCourseStatus = (dateCourse: any, startTime: any, endTime: any) => {
    const now = new Date();

    // On combine la date et l'heure
    const startDateTime = new Date(`${dateCourse}T${startTime}`);
    const endDateTime = new Date(`${dateCourse}T${endTime}`);

    if (now > endDateTime) {
      return "Terminé";
    } else if (now >= startDateTime && now <= endDateTime) {
      return "En cours";
    } else if (now < startDateTime) {
      return "À venir";
    }
  };

  // ajouter des supports de cours
  const AddSupport = async (data: any) => {
    try {
      const formData = new FormData();
      data.forEach((file: any) => formData.append("files", file));
      formData.append("entity_id", id || "");
      formData.append("entity_type", "Course");
      const response = await toast.promise(createFileAttachement(formData), {
        loading: "Importation en cours...",
        success: () => {
          fetchSupports();
          return "Supports ajoutés !";
        },
        error: (error: any) => {
          // console.log("erreur d'ajout de support : ", error);
          return (
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard"
          );
        },
      });
    } catch (error: any) {}
  };

  // Voir/Télécharger des support de cours
  const handleGetSupportFile = async (fileId: string, fileName: string) => {
    try {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "open" }));

      await openSupportInNewTab({ entityType: "Course", fileName });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setFilesLoading((prev: any) => ({ ...prev, [fileId]: "" }));
    }
  };

  const getSortedAndGroupedSessions = (sessions: any[]) => {
    // 1. Tri
    const sortedSessions = [...sessions].sort((a, b) => {
      const dateA = new Date(a.date_course);
      const dateB = new Date(b.date_course);

      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }

      // Si même date, comparer start_time
      if (a.session_start.start_time !== b.session_start.start_time) {
        return a.session_start.start_time.localeCompare(
          b.session_start.start_time,
        );
      }

      // Si même start_time, comparer end_time
      return a.session_end.end_time.localeCompare(b.session_end.end_time);
    });

    // 2. Regroupement par mois
    const grouped = sortedSessions.reduce((acc: any, session) => {
      const date = new Date(session.date_course);
      const monthYear = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
      }).format(date);
      // Capitalize first letter
      const formattedMonthYear =
        monthYear.charAt(0).toUpperCase() + monthYear.slice(1);

      if (!acc[formattedMonthYear]) {
        acc[formattedMonthYear] = [];
      }
      acc[formattedMonthYear].push(session);
      return acc;
    }, {});

    return grouped;
  };

  const availableMonths = useMemo(() => {
    if (!courseDetail?.sessions) return [];
    const months = new Set<string>();
    // On trie d'abord pour avoir les mois dans l'ordre chronologique
    const sorted = [...courseDetail.sessions].sort(
      (a: any, b: any) =>
        new Date(a.date_course).getTime() - new Date(b.date_course).getTime(),
    );

    sorted.forEach((session: any) => {
      const date = new Date(session.date_course);
      const monthYear = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
      }).format(date);
      const formatted = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
      months.add(formatted);
    });
    return Array.from(months);
  }, [courseDetail]);

  // === Gestion du chargement ===
  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <h6 className="text-muted mb-0">Chargement du cours...</h6>
                <div className="mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => window.history.back()}
                  >
                    ← Retour aux cours
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === Gestion du cas sans données ===
  if (!isLoading && hasLoaded && !courseDetail) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="ti ti-inbox fs-2 mb-2 text-muted" />
                <h6 className="text-muted mb-0">
                  Aucun détail de cours disponible.
                </h6>
                <div className="mt-3">
                  <Link
                    to={routes.teacherCourse}
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => window.history.back()}
                  >
                    ← Retour aux cours
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {courseDetail.course.Subject.label}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.teacherCourse}>Cours</Link>
                  </li>
                  <li
                    className="breadcrumb-item active me-2"
                    aria-current="page"
                  >
                    Cours Détails
                  </li>
                  {courseDetail.course.state == "Approuvé" ? (
                    <span className="badge badge-soft-success d-inline-flex align-items-center me-2">
                      <i className="ti ti-circle-filled fs-6 me-1" />
                      Approuvé
                    </span>
                  ) : (
                    <span className="badge badge-soft-warning d-inline-flex align-items-center me-2">
                      <i className="ti ti-circle-filled fs-6 me-1" />
                      Non Approuvé
                    </span>
                  )}
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="d-flex align-items-center justify-content-between p-3 mb-3 border br-5">
            <div className="d-flex align-items-center overflow-hidden me-2">
              <Link to="#" className="avatar avatar-lg flex-shrink-0 br-6 me-2">
                <Avvvatars
                  value={`${
                    courseDetail.course.TeacherProfil.firstname?.charAt(0) || ""
                  }${
                    courseDetail.course.TeacherProfil.lastname?.charAt(0) || ""
                  }`}
                  style="character"
                  size={40}
                />
              </Link>
              <div className="overflow-hidden">
                <h6 className="mb-1 text-truncate">
                  <Link to="#">
                    {courseDetail.course.TeacherProfil.firstname}{" "}
                    {courseDetail.course.TeacherProfil.lastname}
                  </Link>
                </h6>
                <p>
                  {courseDetail.course.Classes.length > 0
                    ? courseDetail.course.Classes.map(
                        (classe: any) =>
                          `${classe.Level.abbreviation} ${classe.abbreviation}`,
                      ).join(", ")
                    : "Aucune Classe"}
                </p>
              </div>
            </div>
          </div>

          <div className="col-xxl-12 col-xl-12">
            <div className="card">
              <div className="card-body">
                <ul
                  className="nav nav-tabs tab-style-2 nav-justified mb-3 d-flex"
                  id="myTab1"
                  role="tablist"
                >
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="seance-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#seance-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="seance-tab-pane"
                      aria-selected="true"
                    >
                      <i className="feather-clock me-1 align-middle" />
                      <span className="d-none d-md-inline">Séances</span>
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link "
                      id="support-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#support-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="support-tab-pane"
                      aria-selected="false"
                    >
                      <i className="feather-file-text me-1 align-middle" />
                      <span className="d-none d-md-inline">Support</span>
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="devoir-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#devoir-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="devoir-tab-pane"
                      aria-selected="false"
                    >
                      <i className="feather-check-square me-1 align-middle" />
                      <span className="d-none d-md-inline">Dévoirs & TD</span>
                    </button>
                  </li>
                </ul>

                <div className="tab-content" id="myTabContent">
                  {/* Liste des séances de ce cours */}
                  <div
                    className="tab-pane fade show active  text-muted"
                    id="seance-tab-pane"
                    role="tabpanel"
                    aria-labelledby="home-tab-1"
                    tabIndex={0}
                  >
                    <div className="col-xl-4 col-xxl-3 theiaStickySidebar">
                      <div className="stickybar">
                        {/* Filtre par mois */}
                        <div className="d-flex justify-content-between mb-4">
                          <label className="form-label fw-semibold">
                            Filtrer par mois
                          </label>
                          <select
                            className="form-select w-50"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                          >
                            <option value="Tous les mois">Tous les mois</option>
                            {availableMonths.map((month) => (
                              <option key={month} value={month}>
                                {month}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Event Item */}
                        {courseDetail.sessions.length > 0 ? (
                          Object.entries(
                            getSortedAndGroupedSessions(courseDetail.sessions),
                          )
                            .filter(
                              ([month]) =>
                                selectedMonth === "Tous les mois" ||
                                month === selectedMonth,
                            )
                            .map(([month, sessions]: [string, any]) => (
                              <div key={month} className="mb-4">
                                <h5 className="mb-3 text-primary ps-3 border-start border-3 border-primary">
                                  {month}
                                </h5>
                                {sessions.map((seance: any, index: number) => {
                                  const courseStatus = getCourseStatus(
                                    seance.date_course,
                                    seance.session_start.start_time,
                                    seance.session_end.end_time,
                                  );
                                  const courseState = seance.state_course;

                                  const courseType = seance.type_course;

                                  const badgeClass =
                                    courseStatus === "Terminé"
                                      ? "badge-soft-success"
                                      : courseStatus === "En cours"
                                        ? "badge-soft-info"
                                        : "badge-soft-warning";

                                  const typeBadgeClass =
                                    courseType === CourseState.NORMAL
                                      ? "badge-soft-primary"
                                      : "badge-soft-warning";

                                  const stateBadgeClass =
                                    courseState === CourseStatus.SCHEDULED
                                      ? "badge-soft-success"
                                      : "badge-soft-danger";

                                  const borderClass =
                                    courseStatus === "Terminé"
                                      ? "border-success"
                                      : courseStatus === "En cours"
                                        ? "border-info"
                                        : "border-warning";

                                  const avatarClass =
                                    courseStatus === "Terminé"
                                      ? "bg-success-transparent text-success"
                                      : courseStatus === "En cours"
                                        ? "bg-info-transparent text-info"
                                        : "bg-warning-transparent text-warning";
                                  return (
                                    <div
                                      key={index}
                                      className={`border-start ${borderClass} border-3 shadow-sm p-3 mb-3 bg-white`}
                                    >
                                      <div className="d-flex flex-column align-items-start mb-3 pb-3 border-bottom">
                                        <div className="d-flex  justify-content-between mb-3 pb-3 w-100">
                                          <span
                                            className={`avatar p-1 me-3 ${avatarClass} flex-shrink-0`}
                                          >
                                            <i className="ti ti-chalkboard fs-20" />
                                          </span>
                                          <div className="flex-fill">
                                            <div className="d-flex justify-content-between align-items-start">
                                              <h6 className="mb-1">
                                                Séance {index + 1}
                                              </h6>

                                              <span
                                                className={`badge d-inline-flex align-items-center ms-2 ${typeBadgeClass}`}
                                              >
                                                <i className="ti ti-circle-filled fs-6 me-1" />
                                                {courseType}
                                              </span>
                                              {/* Badge pour l'état du cours */}
                                              {/* <span
                                                className={`badge d-inline-flex align-items-center ms-2 ${stateBadgeClass}`}
                                              >
                                                <i className="ti ti-circle-filled fs-6 me-1" />
                                                {courseState}
                                              </span> */}
                                              <span
                                                className={`badge d-inline-flex align-items-center ms-2 ${badgeClass}`}
                                              >
                                                <i className="ti ti-circle-filled fs-6 me-1" />
                                                {courseStatus}
                                              </span>
                                            </div>
                                            <p className="fs-12 mb-0">
                                              <i className="ti ti-calendar me-1" />
                                              {seance.classroom.label}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                                          <Link
                                            to={`#`}
                                            className={`btn flex-fill ${
                                              seance.hasAbsence ||
                                              seance.hasLate
                                                ? "btn-success"
                                                : "btn-outline-primary"
                                            }`}
                                            onClick={() => {
                                              setSelectedCourseVersionId(
                                                seance?.ids ?? [],
                                              );
                                              setAbsenceOpen(true);
                                              setSelectSession(seance);
                                            }}
                                          >
                                            <i className="ti ti-user-check me-1" />
                                            Absences
                                            {(seance.hasAbsence ||
                                              seance.hasLate) && (
                                              <i className="ti ti-check ms-1" />
                                            )}
                                          </Link>
                                          <Link
                                            to={`#`}
                                            className={`btn flex-fill ${
                                              seance.hasTracking
                                                ? "btn-success"
                                                : "btn-outline-secondary"
                                            }`}
                                            onClick={() => {
                                              setSelectedCourseVersionId(
                                                seance?.ids ?? [],
                                              );
                                              setSelectSession(seance);
                                              setFollowOpen(true);
                                            }}
                                          >
                                            <i className="ti ti-file-description me-1" />
                                            Suivi
                                            {seance.hasTracking && (
                                              <i className="ti ti-check ms-1" />
                                            )}
                                          </Link>
                                        </div>
                                      </div>

                                      <div className="d-flex align-items-center justify-content-between">
                                        <p className="mb-0 fs-12">
                                          <i className="ti ti-clock me-1" />
                                          {new Date(seance.date_course)
                                            .toLocaleDateString("fr-FR", {
                                              day: "numeric",
                                              month: "long",
                                              year: "numeric",
                                            })
                                            .replace(/^./, (str) =>
                                              str.toUpperCase(),
                                            )}
                                          {" [ " +
                                            seance.session_start.start_time?.slice(
                                              0,
                                              5,
                                            ) +
                                            " - " +
                                            seance.session_end.end_time?.slice(
                                              0,
                                              5,
                                            ) +
                                            " ]"}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ))
                        ) : (
                          <div className="border-start border-secondary border-3 shadow-sm p-3 mb-3 bg-white">
                            <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                              <span className="avatar p-1 me-3 bg-secondary-transparent flex-shrink-0">
                                <i className="ti ti-inbox text-secondary fs-20" />
                              </span>
                              <div className="flex-fill text-center">
                                <h6 className="mb-1">Aucune Séance</h6>
                                <p className="fs-12 mb-0 text-muted">
                                  Il n’y a pas encore de séance programmée pour
                                  ce cours.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* /Event List */}
                    </div>
                  </div>

                  {/* Supports de ce cours */}
                  <div
                    className="tab-pane fade text-muted"
                    id="support-tab-pane"
                    role="tabpanel"
                    aria-labelledby="profile-tab-2"
                    tabIndex={0}
                  >
                    <div className="mb-4 pb-4 border-bottom">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h4>Supports de cours</h4>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.multiple = true;
                            input.accept = "*/*";
                            //  filtrer les fichiers non acceptés
                            input.onchange = async (e: any) => {
                              const files: FileList = e.target.files;
                              const maxSize = 10 * 1024 * 1024; // 10 Mo
                              const oversized: string[] = [];
                              const validFiles: File[] = [];

                              //  filtrer les fichiers non acceptés
                              Array.from(files).forEach((file) => {
                                if (file.size > maxSize) {
                                  oversized.push(file.name);
                                } else {
                                  validFiles.push(file);
                                }
                              });

                              //  afficher une erreur si des fichiers sont trop lourds
                              if (oversized.length) {
                                toast.error(
                                  `Fichier(s) trop lourd(s) (max 10 Mo) : ${oversized.join(
                                    ", ",
                                  )}`,
                                );
                              }
                              //  ajouter les fichiers valides
                              if (validFiles.length) {
                                await AddSupport(validFiles);
                              }
                            };
                            input.click();
                          }}
                        >
                          <i className="ti ti-plus me-1" />
                          Ajouter un support
                        </button>
                      </div>
                      {supports && supports.length > 0 ? (
                        supports.map((file: any) => {
                          const fileExt = file?.filename
                            ?.split(".")
                            .pop()
                            ?.toLowerCase();
                          const iconMap: Record<string, string> = {
                            pdf: "assets/img/icons/pdf-02.svg",
                            xls: "assets/img/icons/xls.svg",
                            xlsx: "assets/img/icons/xls.svg",
                            doc: "assets/img/icons/word.svg",
                            docx: "assets/img/icons/word.svg",
                            ppt: "assets/img/icons/ppt.svg",
                            pptx: "assets/img/icons/ppt.svg",
                          };
                          const iconSrc =
                            iconMap[fileExt] || "assets/img/icons/folder.svg";

                          const formatBytes = (bytes?: number) => {
                            if (!bytes) return " ";
                            if (bytes < 1024) return bytes + " B";
                            const kb = bytes / 1024;
                            if (kb < 1024) return kb.toFixed(1) + " KB";
                            const mb = kb / 1024;
                            return mb.toFixed(1) + " MB";
                          };

                          return (
                            <div
                              key={file.id}
                              className="border rounded-3 bg-white p-3 mb-2"
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                  <ImageWithBasePath
                                    src={iconSrc}
                                    alt="File"
                                    className="me-2 flex-shrink-0"
                                  />
                                  <h5 className="mb-0 text-truncate flex-grow-1">
                                    <Link
                                      to="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleGetSupportFile(
                                          file?.id,
                                          file?.filename,
                                        );
                                      }}
                                    >
                                      {file.filename}
                                    </Link>
                                  </h5>
                                </div>

                                <div className="d-flex align-items-center ms-2 flex-shrink-0">
                                  <div className="dropdown">
                                    <Link
                                      to="#"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      className="dropset"
                                    >
                                      <i className="fa fa-ellipsis-v" />
                                    </Link>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            handleGetSupportFile(
                                              file?.id,
                                              file?.filename,
                                            )
                                          }
                                        >
                                          Télécharger
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            confirmDeleteSupport(file)
                                          }
                                        >
                                          Supprimer
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex align-items-center justify-content-start mt-3">
                                <p className="text-primary mb-0 me-2">
                                  {new Date(file.createdAt).toLocaleDateString(
                                    "fr-FR",
                                  )}
                                </p>
                                <span className="d-flex align-items-center fw-semibold me-2">
                                  <i className="ti ti-circle-filled fs-5 me-2" />
                                  {formatBytes(file.size)}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="border rounded-3 bg-white p-3 mb-2 text-center text-muted">
                          Aucun support de cours disponible.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dévoirs & TD de ce cours */}
                  <div
                    className="tab-pane fade text-muted"
                    id="devoir-tab-pane"
                    role="tabpanel"
                    aria-labelledby="contact-tab"
                    tabIndex={0}
                  >
                    {courseDetail && (
                      <AssignmentsManager
                        courseId={id || ""}
                        classes={courseDetail.course.Classes}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {courseDetail && (
          <SuivisCourseModal
            visible={followOpen}
            onCancel={() => setFollowOpen(false)}
            idsCourseVersionEDT={selectedCourseVersionId}
            onSave={() => fetchCourseDetails()}
          />
        )}

        {courseDetail && (
          <AbsenceCourseModal
            visible={absenceOpen}
            onCancel={() => setAbsenceOpen(false)}
            sessions={allSessions.filter((session: any) => {
              // Trouver les objets session correspondant à session_start et session_end de la session sélectionnée
              const startSession = allSessions.find(
                (s: any) => s?.id === selectSession?.session_start?.id,
              );
              const endSession = allSessions.find(
                (s: any) => s?.id === selectSession?.session_end?.id,
              );

              if (!startSession || !endSession) return false;

              // Convert "hh:mm:ss" strings to comparable numbers (minutes since 00:00)
              const parseTime = (t: string) => {
                const [h, m] = t.split(":").map(Number);
                return h * 60 + m;
              };

              const startTime = parseTime(startSession.start_time);
              const endTime = parseTime(endSession.end_time);
              const sessionStart = parseTime(session.start_time);
              const sessionEnd = parseTime(session.end_time);

              // Retourner vrai si l’heure de début et de fin de la session courante est comprise dans l’intervalle
              return sessionStart >= startTime && sessionEnd <= endTime;
            })}
            idsCourseVersionEDT={selectedCourseVersionId}
            idsClass={courseDetail?.course?.Classes}
          />
        )}
      </div>
    </>
  );
};

export default TeacherCourseDetails;
